import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { SingleAssetMetricPanelConfig } from '@src/types/LayoutConfig';
import { useCallback, useRef } from 'react';
import { Panel } from '../../../Layout/Components/Panel';
import type { LayoutPanelProps } from '../../types';
import { SingleAssetMetricModule } from './SingleAssetMetric/SingleAssetMetricModule';
import { DEFAULT_METRIC_FULL_NAME } from './SingleAssetMetric/useMarketCapData';
import type { SignalsTimeRange } from './core';

const DEFAULT_SYMBOL = 'BTC';

export function SingleAssetMetricPanel({ api, params }: LayoutPanelProps<SingleAssetMetricPanelConfig>) {
  const { enableSignals } = useFeatureFlag();

  // Tracks the latest resolved full metric name so that when the symbol changes
  // the title can be rebuilt without an extra network call.
  const fullMetricNameRef = useRef(DEFAULT_METRIC_FULL_NAME);

  const handleSymbolChange = useCallback(
    (symbol: string) => {
      api.updateParameters({ symbol });
      if (!params.customTitle) {
        api.setTitle(`${symbol} - ${fullMetricNameRef.current}`);
      }
    },
    [api, params.customTitle]
  );

  const handleMetricChange = useCallback(
    (metric: string, fullMetricName: string) => {
      fullMetricNameRef.current = fullMetricName;
      if (metric !== params.metric) {
        api.updateParameters({ metric });
      }
      if (!params.customTitle) {
        api.setTitle(`${params.symbol ?? DEFAULT_SYMBOL} - ${fullMetricName}`);
      }
    },
    [api, params.customTitle, params.metric, params.symbol]
  );

  const handleTimeRangeChange = useCallback(
    (timeRange: SignalsTimeRange) => {
      api.updateParameters({ timeRange });
    },
    [api]
  );

  if (!enableSignals) {
    return null;
  }

  return (
    <Panel panelApi={api} data-testid="market-cap-estimated-panel">
      {/* TODO(space-filter): pass spaceAsset from the space context once the space-filter system lands. */}
      <SingleAssetMetricModule
        symbol={params.symbol}
        metric={params.metric}
        timeRange={params.timeRange}
        onSymbolChange={handleSymbolChange}
        onMetricChange={handleMetricChange}
        onTimeRangeChange={handleTimeRangeChange}
      />
    </Panel>
  );
}
